import type { ComponentType } from 'preact';
import {
  Rabbit,
  Cat,
  Bird,
  Squirrel,
  Turtle,
  Feather,
  TreePine,
  Waves,
  Castle,
  Rocket,
  BookOpen,
  Compass,
  Moon,
  Flame,
  Gem,
  CircleDot,
  Fish,
  Snowflake,
  Mouse,
  Flower2,
  Mountain,
  Home,
  Sun,
  Search,
  Users,
  type LucideProps,
} from 'lucide-preact';
import type { StoryType, Animal, Setting } from './types';

export type IconComponent = ComponentType<LucideProps>;

export interface StoryOptionWithIcon<T extends string> {
  value: T;
  label: string;
  icon: IconComponent;
  description?: string;
  color: string; // Tailwind color class
}

export const STORY_TYPES: StoryOptionWithIcon<StoryType>[] = [
  {
    value: 'fable',
    label: 'Fable',
    icon: BookOpen,
    description: 'A story with a wise lesson',
    color: 'amber',
  },
  {
    value: 'adventure',
    label: 'Adventure',
    icon: Compass,
    description: 'An exciting journey',
    color: 'emerald',
  },
  {
    value: 'bedtime',
    label: 'Bedtime',
    icon: Moon,
    description: 'A calm, peaceful tale',
    color: 'indigo',
  },
  {
    value: 'mystery',
    label: 'Mystery',
    icon: Search,
    description: 'A puzzle to solve',
    color: 'violet',
  },
  {
    value: 'friendship',
    label: 'Friendship',
    icon: Users,
    description: 'A heartwarming bond',
    color: 'rose',
  },
];

// Heroes are all magical animals/creatures - no humans to avoid representation issues
export const HEROES: StoryOptionWithIcon<Animal>[] = [
  {
    value: 'rabbit',
    label: 'Brave Bunny',
    icon: Rabbit,
    color: 'pink',
    description: 'Quick and clever',
  },
  {
    value: 'fox',
    label: 'Curious Fox',
    icon: Cat,
    color: 'orange',
    description: 'Smart and resourceful',
  },
  {
    value: 'owl',
    label: 'Wise Owl',
    icon: Bird,
    color: 'violet',
    description: 'Thoughtful and kind',
  },
  {
    value: 'bear',
    label: 'Gentle Bear',
    icon: Squirrel,
    color: 'amber',
    description: 'Strong and caring',
  },
  {
    value: 'turtle',
    label: 'Steady Turtle',
    icon: Turtle,
    color: 'teal',
    description: 'Patient and wise',
  },
  {
    value: 'deer',
    label: 'Swift Deer',
    icon: Feather,
    color: 'rose',
    description: 'Graceful and brave',
  },
  {
    value: 'dragon',
    label: 'Friendly Dragon',
    icon: Flame,
    color: 'orange',
    description: 'Magical and warm',
  },
  {
    value: 'unicorn',
    label: 'Gentle Unicorn',
    icon: Gem,
    color: 'pink',
    description: 'Pure and kind',
  },
  {
    value: 'hedgehog',
    label: 'Cozy Hedgehog',
    icon: CircleDot,
    color: 'amber',
    description: 'Shy and sweet',
  },
  {
    value: 'otter',
    label: 'Playful Otter',
    icon: Fish,
    color: 'teal',
    description: 'Fun and joyful',
  },
  {
    value: 'penguin',
    label: 'Plucky Penguin',
    icon: Snowflake,
    color: 'slate',
    description: 'Brave and loyal',
  },
  {
    value: 'mouse',
    label: 'Little Mouse',
    icon: Mouse,
    color: 'amber',
    description: 'Tiny and mighty',
  },
];

// Keep ANIMALS as alias for backwards compatibility
export const ANIMALS = HEROES;

export const SETTINGS: StoryOptionWithIcon<Setting>[] = [
  {
    value: 'forest',
    label: 'Enchanted Forest',
    icon: TreePine,
    color: 'green',
    description: 'Tall trees and sparkling streams',
  },
  {
    value: 'ocean',
    label: 'Deep Ocean',
    icon: Waves,
    color: 'blue',
    description: 'Colorful fish and hidden treasures',
  },
  {
    value: 'castle',
    label: 'Cloud Castle',
    icon: Castle,
    color: 'purple',
    description: 'Tall towers and secret passages',
  },
  {
    value: 'space',
    label: 'Starry Cosmos',
    icon: Rocket,
    color: 'slate',
    description: 'Twinkling stars and friendly planets',
  },
  {
    value: 'meadow',
    label: 'Sunny Meadow',
    icon: Flower2,
    color: 'amber',
    description: 'Wildflowers and gentle breezes',
  },
  {
    value: 'mountain',
    label: 'Misty Mountain',
    icon: Mountain,
    color: 'slate',
    description: 'High peaks and hidden caves',
  },
  {
    value: 'village',
    label: 'Cozy Village',
    icon: Home,
    color: 'orange',
    description: 'Friendly neighbors and warm hearths',
  },
  {
    value: 'garden',
    label: 'Secret Garden',
    icon: Flower2,
    color: 'pink',
    description: 'Magical blooms and tiny friends',
  },
  {
    value: 'arctic',
    label: 'Frozen North',
    icon: Snowflake,
    color: 'blue',
    description: 'Shimmering ice and aurora lights',
  },
  {
    value: 'desert',
    label: 'Golden Desert',
    icon: Sun,
    color: 'amber',
    description: 'Sand dunes and oasis dreams',
  },
];

export const MAX_SAVED_STORIES = 10;
export const MAX_NAME_LENGTH = 20;

// Color mappings for dynamic styling
export const COLOR_CLASSES: Record<
  string,
  { bg: string; bgHover: string; text: string; border: string; ring: string }
> = {
  amber: {
    bg: 'bg-amber-50',
    bgHover: 'hover:bg-amber-100',
    text: 'text-amber-700',
    border: 'border-amber-200',
    ring: 'ring-amber-500',
  },
  emerald: {
    bg: 'bg-emerald-50',
    bgHover: 'hover:bg-emerald-100',
    text: 'text-emerald-700',
    border: 'border-emerald-200',
    ring: 'ring-emerald-500',
  },
  indigo: {
    bg: 'bg-indigo-50',
    bgHover: 'hover:bg-indigo-100',
    text: 'text-indigo-700',
    border: 'border-indigo-200',
    ring: 'ring-indigo-500',
  },
  pink: {
    bg: 'bg-pink-50',
    bgHover: 'hover:bg-pink-100',
    text: 'text-pink-700',
    border: 'border-pink-200',
    ring: 'ring-pink-500',
  },
  orange: {
    bg: 'bg-orange-50',
    bgHover: 'hover:bg-orange-100',
    text: 'text-orange-700',
    border: 'border-orange-200',
    ring: 'ring-orange-500',
  },
  violet: {
    bg: 'bg-violet-50',
    bgHover: 'hover:bg-violet-100',
    text: 'text-violet-700',
    border: 'border-violet-200',
    ring: 'ring-violet-500',
  },
  teal: {
    bg: 'bg-teal-50',
    bgHover: 'hover:bg-teal-100',
    text: 'text-teal-700',
    border: 'border-teal-200',
    ring: 'ring-teal-500',
  },
  rose: {
    bg: 'bg-rose-50',
    bgHover: 'hover:bg-rose-100',
    text: 'text-rose-700',
    border: 'border-rose-200',
    ring: 'ring-rose-500',
  },
  green: {
    bg: 'bg-green-50',
    bgHover: 'hover:bg-green-100',
    text: 'text-green-700',
    border: 'border-green-200',
    ring: 'ring-green-500',
  },
  blue: {
    bg: 'bg-blue-50',
    bgHover: 'hover:bg-blue-100',
    text: 'text-blue-700',
    border: 'border-blue-200',
    ring: 'ring-blue-500',
  },
  purple: {
    bg: 'bg-purple-50',
    bgHover: 'hover:bg-purple-100',
    text: 'text-purple-700',
    border: 'border-purple-200',
    ring: 'ring-purple-500',
  },
  slate: {
    bg: 'bg-slate-50',
    bgHover: 'hover:bg-slate-100',
    text: 'text-slate-700',
    border: 'border-slate-200',
    ring: 'ring-slate-500',
  },
};
